/* ── Panel ── */
.panel {
  position: fixed;
  top: 68px;
  right: var(--nexus-space-md);
  width: 400px;
  max-height: calc(100dvh - 68px - var(--nexus-space-md));
  display: flex;
  flex-direction: column;
  background: var(--nexus-bg);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-surface);
  box-shadow: var(--nexus-shadow-dropdown);
  z-index: 500;
  animation: panelFadeIn 120ms ease-out;
  overflow: hidden;
}

@keyframes panelFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Header ── */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nexus-space-sm);
  padding: var(--nexus-space-sm) var(--nexus-space-sm) var(--nexus-space-sm) var(--nexus-space-md);
  border-bottom: 1px solid var(--nexus-border);
  flex-shrink: 0;
}

.headerLeft {
  display: flex;
  align-items: baseline;
  gap: var(--nexus-space-sm);
}

.headerTitle {
  font-size: var(--nexus-text-base);
  font-weight: var(--nexus-weight-semibold);
  color: var(--nexus-text);
}

.headerCount {
  font-size: var(--nexus-text-sm);
  color: var(--nexus-text-secondary);
}

.headerActions {
  display: flex;
  gap: 2px;
}

.headerBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--nexus-radius-control);
  border: none;
  background: transparent;
  color: var(--nexus-text-secondary);
  cursor: pointer;
  transition: color var(--nexus-transition-fast), background var(--nexus-transition-fast);
}

.headerBtn:hover {
  color: var(--nexus-text);
  background: var(--nexus-surface-hover);
}

/* ── List ── */
.list {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* scrollbar */
.list::-webkit-scrollbar {
  width: 6px;
}

.list::-webkit-scrollbar-track {
  background: transparent;
}

.list::-webkit-scrollbar-thumb {
  background: var(--nexus-overlay-medium);
  border-radius: var(--nexus-radius-full);
}

/* ── Empty state ── */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nexus-space-xs);
  padding: var(--nexus-space-2xl) var(--nexus-space-lg) var(--nexus-space-xl);
  text-align: center;
}

.emptyIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: var(--nexus-space-xs);
  border-radius: var(--nexus-radius-full);
  background: var(--nexus-surface);
  color: var(--nexus-text-secondary);
}

.emptyTitle {
  font-size: var(--nexus-text-base);
  font-weight: var(--nexus-weight-semibold);
  color: var(--nexus-text);
}

.emptyBody {
  font-size: var(--nexus-text-sm);
  color: var(--nexus-text-secondary);
  line-height: 1.4;
}

/* ── Day sections (dividers only — order stays newest first) ── */
.day {
  margin: 0;
  padding: var(--nexus-space-md) var(--nexus-space-md) var(--nexus-space-xs);
  font-size: var(--nexus-text-xs);
  font-weight: var(--nexus-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nexus-text-muted);
}

/* ── Notification row (SwipeableRow wrapper) ── */
.row + .row {
  border-top: 1px solid var(--nexus-overlay-subtle);
}

/* ── Notification item ── */
.item {
  position: relative;
  display: flex;
  flex-direction: column;
  padding-bottom: var(--nexus-space-sm);
  background: var(--nexus-bg);
  color: var(--nexus-text);
  transition: background var(--nexus-transition-fast);
}

.item:hover {
  /* One step under surface-hover so the secondary action button keeps its edge */
  background: var(--nexus-surface);
}

.item:hover .kind {
  border-color: var(--nexus-surface);
}

.unreadDot {
  position: absolute;
  left: 9px;
  /* Level with the middle of the source tile */
  top: calc(var(--nexus-space-sm) + 15px);
  width: 6px;
  height: 6px;
  border-radius: var(--nexus-radius-full);
  background: var(--nexus-purple);
}

/* Main clickable area */
.itemMain {
  display: flex;
  align-items: flex-start;
  gap: var(--nexus-space-sm);
  width: 100%;
  padding: var(--nexus-space-sm) var(--nexus-space-md) 0 22px;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-align: left;
}

/* ── Source tile: who sent it, with the kind of notice as a badge ── */
.source {
  position: relative;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--nexus-radius-surface);
  object-fit: cover;
  background: var(--nexus-surface);
}

.tileCarousel {
  background: var(--nexus-surface-hover);
}

.tileMark {
  width: 16px;
  height: 20px;
  object-fit: contain;
}

.tileInitial {
  color: var(--nexus-text-secondary);
  font-weight: var(--nexus-weight-bold);
  font-size: var(--nexus-text-sm);
}

.kind {
  position: absolute;
  right: -5px;
  bottom: -5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: var(--nexus-radius-full);
  background: var(--nexus-surface-hover);
  border: 2px solid var(--nexus-bg);
  color: var(--nexus-text);
}

/* ── Item content ── */
.itemContent {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.itemTop {
  display: flex;
  align-items: baseline;
  gap: var(--nexus-space-sm);
}

.itemTitle {
  flex: 1;
  min-width: 0;
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-semibold);
  color: var(--nexus-text);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.repeat {
  margin-left: var(--nexus-space-xs);
  padding: 0 5px;
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-control);
  font-size: var(--nexus-text-xs);
  font-weight: var(--nexus-weight-medium);
  color: var(--nexus-text-secondary);
}

.time {
  font-size: var(--nexus-text-xs);
  color: var(--nexus-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.itemMeta {
  font-size: var(--nexus-text-xs);
  color: var(--nexus-text-muted);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.orgName {
  color: var(--nexus-text-secondary);
  font-weight: var(--nexus-weight-medium);
}

/* What the notice asks of you, under the text so titles keep their width */
.rowAction {
  align-self: flex-start;
  margin: var(--nexus-space-sm) 0 0 calc(22px + 36px + var(--nexus-space-sm));
}

/* Mark read / dismiss: over the time on hover, always there on touch */
.itemActions {
  position: absolute;
  top: 6px;
  right: var(--nexus-space-sm);
  display: flex;
  gap: 2px;
  padding-left: var(--nexus-space-xs);
  background: var(--nexus-surface);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--nexus-transition-fast);
}

.item:hover .itemActions,
.item:focus-within .itemActions {
  opacity: 1;
  pointer-events: auto;
}

.itemActionBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--nexus-radius-sm);
  border: none;
  background: transparent;
  color: var(--nexus-text-muted);
  cursor: pointer;
  transition: color var(--nexus-transition-fast), background var(--nexus-transition-fast);
}

.itemActionBtn:hover {
  color: var(--nexus-text);
  background: var(--nexus-overlay-medium);
}

/* ── Footer ── */
.footer {
  display: flex;
  border-top: 1px solid var(--nexus-border);
  flex-shrink: 0;
}

.footerLink {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--nexus-space-xs);
  height: 44px;
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-medium);
  color: var(--nexus-text);
  background: none;
  border: none;
  cursor: pointer;
  transition: background var(--nexus-transition-fast);
}

.footerLink:hover {
  background: var(--nexus-surface-hover);
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .panel {
    top: 60px;
    left: var(--nexus-space-sm);
    right: var(--nexus-space-sm);
    width: auto;
    max-height: calc(100dvh - 60px - var(--nexus-space-md));
  }

  .list {
    -webkit-overflow-scrolling: touch;
  }

  /* Swipe does read / delete on a phone; no hover to reveal the buttons */
  .itemActions {
    display: none;
  }
}
